import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Radio, Icon, Checkbox
} from 'semantic-ui-react';
import {
  FULL_BLEED, MATTE, FLOAT_DECKLED, FLOAT_STRIGHT
} from 'arts/components/PapersMenu/mounts';
import PanelLayout from '../panel/layout';
import SelectableFrame from './selectable_frame';
import * as shared from '../assets/shared.module.scss';
import * as classes from './classes.module.scss';
import More from '../assets/more';
import Remove from '../assets/remove';
import Bag from '../assets/bag';
import WorkTitle from './work_title';
import Const from '../../config/const';
import { sortedHumanSizes } from '../../config/utils';

const NO_FRAME = 'noFrame';

const bestOptionFor = (obj, sequence) => {
  for (let i = 0, l = sequence.length; i < l; i += 1) {
    if (obj[sequence[i]]) {
      return sequence[i];
    }
  }
  return sequence[0];
};

const buildFrameGroups = (options, frameId) => {
  const res = {
    [NO_FRAME]: options[0]
  };
  options.forEach((o) => {
    if (o.frameId) {
      const matKey = o.mount;
      res[matKey] = res[matKey] || {};
      res[matKey][o.frameCategory] = res[matKey][o.frameCategory] || [];
      res[matKey][o.frameCategory].push(o);
      if (frameId === o.frameId) {
        res.currentMount = matKey;
        res.currentMatColor = o.frameMatColorName;
      }
    }
  });
  res.currentMount = res.currentMount || bestOptionFor(res, ['matte', 'fullBleed', NO_FRAME]);
  // noFrame, 0, 3, currentMount
  return res;
};

const filterOptions = (options, matColor) => {
  const useMatColor = matColor || 'White';
  const res = {};
  Object.keys(options).forEach((key) => {
    res[key] = options[key].filter((o) => o.frameMatColorName === useMatColor);
  });
  return res;
};

export default class WorkDetails extends PureComponent {
  static propTypes = {
    workId: PropTypes.number,
    workSizeId: PropTypes.number,
    frameId: PropTypes.number,
    nonGlare: PropTypes.bool,
    index: PropTypes.number,
    workSizes: PropTypes.array,
    setWorkSize: PropTypes.func.isRequired,
    removeFromCanvas: PropTypes.func.isRequired,
    setFrame: PropTypes.func.isRequired,
    setNonGlare: PropTypes.func.isRequired,
    hide: PropTypes.func.isRequired,
    showMoreLikeThis: PropTypes.func.isRequired,
    addToCart: PropTypes.func.isRequired,
    framingOptions: PropTypes.array.isRequired,
    landscape: PropTypes.bool,
    imageUrl: PropTypes.string,
    title: PropTypes.string,
    authorName: PropTypes.string,
    isHidden: PropTypes.bool
  };

  constructor(props) {
    super(props);
    const { framingOptions, frameId } = props;
    this.state = buildFrameGroups(framingOptions, frameId);
    this.setFrameFromOption = this.setFrameFromOption.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { framingOptions, frameId } = this.props;
    if (prevProps.framingOptions !== framingOptions) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(buildFrameGroups(framingOptions, frameId));
    }
  }

  setFrameFromOption(frameId, mount, frameMatColorName) {
    const { setFrame, index } = this.props;
    setFrame(frameId, index);
    this.setState({ currentMount: mount, currentMatColor: frameMatColorName });
  }

  setMount(mount, frameMatColorName) {
    const {
      frameId, setFrame, index, workSizeId
    } = this.props;
    const newFrameId = this.findFirstMatchingFrameId(
      workSizeId, workSizeId, mount, frameMatColorName
    );
    if (frameId !== newFrameId) {
      setFrame(newFrameId, index);
    }
    this.setState({ currentMount: mount, currentMatColor: frameMatColorName });
  }

  toggleNonGlare = () => {
    const { setNonGlare, index, nonGlare } = this.props;
    setNonGlare(!nonGlare, index);
  }

  setNoFrame = () => {
    const { setFrame, index } = this.props;
    setFrame(null, index);
    this.setState({ currentMatColor: null, currentMount: MATTE });
  };

  removeWork = () => {
    const { hide, index, removeFromCanvas } = this.props;
    removeFromCanvas(index);
    hide();
  }

  setWorkSize = (newId) => {
    const { currentMount, currentMatColor } = this.state;
    const {
      index, setFrame, setWorkSize, workSizeId
    } = this.props;
    const newFrameId = this.findFirstMatchingFrameId(
      workSizeId, newId, currentMount, currentMatColor
    );
    setWorkSize(newId, index);
    if (newFrameId) {
      setFrame(newFrameId, index);
    }
  }

  addToCart = () => {
    const { addToCart, index } = this.props;
    addToCart(index);
  }

  showMoreLikeThis = () => {
    const { showMoreLikeThis, workId, index } = this.props;
    showMoreLikeThis(workId, index, Const.moreLikeThisSources.canvas);
  }

  findFirstMatchingFrameId(prevWorkSize, newWorkSizeId, mount, frameMatColorName) {
    const {
      frameId, workSizes
    } = this.props;
    const ws = (workSizes || []).find((w) => w.id === prevWorkSize);
    let option;
    if (!frameId) {
      option = ws?.options?.find((o) => !!o.frameId);
    } else {
      option = ws?.options?.find((o) => o.frameId === frameId);
    }
    if (!option) {
      return null;
    }
    const { frameName } = option;
    const newWs = (workSizes || []).find((w) => w.id === newWorkSizeId);
    const altOption = newWs.options.find(
      (o) => (
        o.frameName === frameName
          && o.mount === mount
          && o.frameMatColorName === frameMatColorName
      )
    );
    if (altOption) {
      return altOption.frameId;
    }
    const mountAndMatOption = newWs.options.find(
      (o) => (
        o.mount === mount && o.frameMatColorName === frameMatColorName
      )
    );
    if (mountAndMatOption) {
      return mountAndMatOption.frameId;
    }
    const mountOption = newWs.options.find((o) => o.mount === mount);
    if (mountOption) {
      return mountOption.frameId;
    }
    return null;
  }

  renderWorkSizes() {
    const {
      workSizes, workSizeId, landscape
    } = this.props;
    return sortedHumanSizes(workSizes || [], landscape).map(([hs, ws]) => (
      <Button
        // basic={ ws.id !== workSizeId }
        key={ ws.id }
        onClick={ () => this.setWorkSize(ws.id) }
        color={ ws.id === workSizeId ? 'black' : null }
      >
        { ws.isHidden ? <Icon name="eye slash" /> : null }
        { hs }
      </Button>
    ));
  }

  renderCategory(name, options) {
    const { frameId  } = this.props;
    if (!options) {
      return null;
    }
    return (
      <div key={ name } className={ classes.category }>
        <h4 className={ shared.h4 }>
          { name }
        </h4>
        <div className={ classes.section }>
          {
            options.map((o) => (
              <SelectableFrame
                key={ o.frameId }
                { ...o }
                currentFrameId={ frameId }
                setFrameFromOption={ this.setFrameFromOption }
              />
            ))
          }
        </div>
      </div>
    );
  }

  renderMountSelection(mount, label) {
    const { framingOptions, frameId } = this.props;
    const { currentMount, currentMatColor } = this.state;
    if (framingOptions.findIndex((o) => o.mount === mount) === -1) {
      // no frames with this kind
      return null;
    }
    const availableMatColors = mount === MATTE
      ? [...new Set(framingOptions.filter(o => o.frameMatColorName).map(o => o.frameMatColorName))]
      : [];
    if (mount === MATTE) {
      return (
        <>
          {
            availableMatColors.map((matColor) => (
              <Radio
                label={ `${ label } - ${ matColor }` }
                key={ `${ label } - ${ matColor }` }
                name="matt"
                value={ matColor }
                checked={ currentMatColor === matColor }
                onChange={ () => this.setMount(mount, matColor) }
              />
            ))
          }
        </>
      );
    }

    return (
      <Radio
        label={ label }
        name="matt"
        value={ mount }
        checked={ currentMount === mount && !!frameId }
        onChange={ () => this.setMount(mount, null) }
      />
    );
  }

  render() {
    const {
      workId, frameId, hide, title, authorName, imageUrl, isHidden, nonGlare, framingOptions
    } = this.props;
    if (!workId) {
      return null;
    }

    const selectedFramingOption = framingOptions.find((o) => o.frameId === frameId);

    const { currentMount, currentMatColor } = this.state;
    // eslint-disable-next-line react/destructuring-assignment
    const options = currentMount === NO_FRAME
      ? {}
      : (this.state[currentMount] || {});
    const filteredOptions = currentMount === MATTE
      ? filterOptions(options, currentMatColor)
      : options;

    const selectFrame = Object.keys(options).length ? (
      <>
        <div className={ classes.mounts }>
          <Radio
            label="No Frame"
            name="matt"
            value="nf"
            checked={ frameId === null }
            onChange={ this.setNoFrame }
          />
          { this.renderMountSelection(MATTE, 'With Matte') }
          { this.renderMountSelection(FULL_BLEED, 'No Matte') }
          { this.renderMountSelection(FLOAT_STRIGHT, 'Float Mount - Straight Edge') }
          { this.renderMountSelection(FLOAT_DECKLED, 'Float Mount - Deckled Edge') }
        </div>
        {
          frameId && selectedFramingOption?.nonGlarePrice ? (
            <>
              <p>UV Plexiglass</p>
              <Checkbox
                label="Non-Glare"
                name="nonGlare"
                value="nonGlare"
                checked={ nonGlare }
                onChange={ this.toggleNonGlare }
              />
            </>
          ) : null
        }
        <div className={ classes.options }>
          { Object.keys(filteredOptions).map((c) => this.renderCategory(c, filteredOptions[c])) }
        </div>
      </>
    ) : null;

    const renderTitle = imageUrl ? (
      <WorkTitle
        imageUrl={ imageUrl }
        title={ title }
        authorName={ authorName }
        isHidden={ isHidden }
      />
    ) : null;

    return (
      <PanelLayout onClose={ hide } title="Design">
        <div className={ classes.content }>
          <div className={ shared.holder }>
            <div className={ classes.scroll }>
              <div className={ classes.actions }>
                <button className={ classes.action } type="button" name="more" onClick={ this.showMoreLikeThis }>
                  <More fill="#000" />
                  More like this
                </button>
                <div className={ classes.divider } />
                <button className={ classes.action } type="button" name="more" onClick={ this.addToCart }>
                  <Bag fill="#000" />
                  Add to cart
                </button>
                <div className={ classes.divider } />
                <button className={ classes.action } type="button" name="more" onClick={ this.removeWork }>
                  <Remove fill="#000" />
                  Remove
                </button>
              </div>
              { renderTitle }
              <div className={ classes.options }>
                { this.renderWorkSizes() }
              </div>
              { selectFrame }
            </div>
          </div>
        </div>
      </PanelLayout>
    );
  }
}
